import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';

// Banner images for the login page
const desktopBannerUrl = "/images/banner2.jpg";
const mobileBannerUrl = "/images/banner.jpg";

const LoginPage = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const { darkMode } = useContext(ThemeContext);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!credentials.username || !credentials.password) {
      setError('Silahkan masukkan nama pengguna dan kata sandi');
      return;
    }

    const success = await onLogin(credentials);
    if (!success) {
      setError('Nama pengguna atau kata sandi tidak valid');
    }
  };

  return (
    <LoginContainer>
      <LoginWrapper>
        <ImageSection>
          <GradientOverlay>
            <WelcomeText>
              <h1>SIMMICO ARCHIVE 2025-2026</h1>
              <p>Sistem Informasi Michael College</p>
            </WelcomeText>
          </GradientOverlay>
        </ImageSection>
        
        <FormSection>
          <FormWrapper>
            <LogoContainer>
              <LogoImage src="/images/logo.png" alt="SIMICO Logo" />
            </LogoContainer>
            
            <LoginForm onSubmit={handleSubmit}>
              <h2>Login</h2>
              <LoginSubtitle>Masukkan kredensial untuk mengakses dasbor</LoginSubtitle>
              
              {error && <ErrorMessage theme={darkMode ? 'dark' : 'light'}>{error}</ErrorMessage>}
              
              <FormGroup>
                <label htmlFor="username">Email</label>
                <InputWithIcon>
                  <i>👤</i>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={credentials.username}
                    onChange={handleChange}
                    placeholder="Masukkan email"
                    required
                  />
                </InputWithIcon>
              </FormGroup>
              
              <FormGroup>
                <label htmlFor="password">Password</label>
                <InputWithIcon>
                  <i>🔒</i>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={credentials.password}
                    onChange={handleChange}
                    placeholder="Masukkan password"
                    required
                  />
                </InputWithIcon>
              </FormGroup>
              
              <ForgotPasswordContainer>
                <ForgotPasswordLink to="/forget-password">
                  Reset Password
                </ForgotPasswordLink>
              </ForgotPasswordContainer>
              
              <LoginButton type="submit">Masuk ke Dasbor</LoginButton>
                 
              <HelpText>
                Butuh bantuan? Hubungi <a target="_blank" rel="noreferrer" href="https://api.whatsapp.com/send/?phone=6281329314116&text&type=phone_number&app_absent=0">IT Support</a>
                <br />Version 1.5.0
              </HelpText>
            </LoginForm>
          </FormWrapper>
        </FormSection>
      </LoginWrapper>
    </LoginContainer>
  );
};

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--background-light);
  padding: 1rem;
`;

const LoginWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  min-height: 600px;
  background-color: var(--background);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--box-shadow);
  
  @media (max-width: 992px) {
    flex-direction: column;
    max-width: 600px;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  background-image: url(${desktopBannerUrl});
  background-size: cover;
  background-position: center;
  position: relative;
  
  @media (max-width: 992px) {
    min-height: 200px;
    background-image: url(${mobileBannerUrl});
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(43, 93, 161, 0.5) 0%, rgba(43, 93, 161, 0.3) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 3rem;
  
  @media (max-width: 992px) {
    padding: 1.5rem;
  }
`;

const WelcomeText = styled.div`
  color: white;
  
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  p {
    font-size: 1.2rem;
    font-weight: 300;
    max-width: 400px;
    opacity: 0.9;
  }
  
  @media (max-width: 1200px) {
    h1 {
      font-size: 2rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 992px) {
    h1 {
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }
    
    p {
      font-size: 0.875rem;
    }
  }
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 576px) {
    padding: 1.5rem;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 400px;
`;

const LogoContainer = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`;

const LogoImage = styled.img`
  height: 80px;
  max-width: 100%;
  object-fit: contain;
  margin-bottom: 0.5rem;
`;

const LoginForm = styled.form`
  h2 {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
    color: var(--text-dark);
    font-weight: 600;
  }
`;

const LoginSubtitle = styled.p`
  color: var(--text-light);
  margin-bottom: 2rem;
  font-size: 0.875rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
    font-size: 0.9375rem;
  }
`;

const InputWithIcon = styled.div`
  position: relative;
  
  i {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-lighter);
    font-style: normal;
    font-size: 1.1rem;
  }
  
  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s;
    background-color: var(--background-light);
    color: var(--text-dark);
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(var(--primary-rgb, 43, 93, 161), 0.2);
      background-color: var(--background);
    }
  }
`;

const ForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1.5rem;
  font-size: 0.9375rem;
`;

const ForgotPasswordLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.875rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 10px rgba(43, 93, 161, 0.2);
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(43, 93, 161, 0.25);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 8px rgba(43, 93, 161, 0.2);
  }
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme === 'dark' ? 'rgba(220, 53, 69, 0.2)' : '#feeeee'};
  color: var(--danger);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  border-left: 3px solid var(--danger);
  display: flex;
  align-items: center;
  
  &:before {
    content: "⚠️";
    margin-right: 0.5rem;
  }
`;

const HelpText = styled.div`
  text-align: center;
  margin-top: 2rem;
  font-size: 0.875rem;
  color: var(--text-light);
  
  a {
    color: var(--primary);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

export default LoginPage;